import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Image,
  Input,
  Text,
  useToast,
} from "@chakra-ui/react";
import Layout from "../components/Layout";
import { useForm } from "react-hook-form";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useDropzone } from "react-dropzone";
import { AttachmentIcon, DeleteIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";
import client from "../utils/client";
import { getImageUploadUrl } from "../utils/s3";
import axios from "axios";

const CreateBlog = () => {
  const { postId } = useParams();
  const isEditMode = !!postId;

  const { register, handleSubmit, setValue } = useForm();
  const [text, setText] = useState("");
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEditMode);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isEditMode) return;
    client
      .get(`/blog/${postId}`)
      .then((res) => {
        setValue("title", res.data.title);
        setText(res.data.text);
        setExistingImages(res.data.images || []);
      })
      .catch(() =>
        toast({ title: "Erro ao carregar publicação", status: "error", duration: 5000, isClosable: true })
      )
      .finally(() => setIsFetching(false));
  }, [postId, isEditMode, setValue, toast]);

  const onDrop = (acceptedFiles) => {
    setImages((prev) => [
      ...prev,
      ...acceptedFiles.map((f) => Object.assign(f, { preview: URL.createObjectURL(f) })),
    ]);
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: "image/*" });

  async function uploadFile(file, key) {
    const signedUrl = await getImageUploadUrl(key, file.type);
    await axios.put(signedUrl, file, { headers: { "Content-Type": file.type } });
  }

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const newImageUrls = [];
      for (const file of images) {
        const filename = `newsPhoto-${v4()}.jpeg`;
        await uploadFile(file, `news_images/${filename}`);
        newImageUrls.push(filename);
      }
      const allImages = [...existingImages, ...newImageUrls];

      if (isEditMode) {
        await client.patch(`/blog/${postId}`, { title: data.title, text, images: allImages });
        toast({ title: "Publicação atualizada!", status: "success", duration: 5000, isClosable: true });
      } else {
        await client.post("/blog", { title: data.title, text, images: allImages });
        toast({ title: "Publicação criada!", status: "success", duration: 5000, isClosable: true });
      }
      navigate("/allBlog");
    } catch (error) {
      console.error(error);
      toast({ title: "Algum erro aconteceu", status: "error", duration: 9000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <Layout>
        <Flex justifyContent="center" alignItems="center" minH="50vh">
          <Text color="gray.400">Carregando...</Text>
        </Flex>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box maxW="900px" mx="auto" px={[4, 6, 8]} py={[6, 10]}>
        <Flex alignItems="center" justifyContent="space-between" mb={6}>
          <Box>
            <Heading
              fontSize={["1.4rem", "1.8rem"]}
              color="green.700"
              borderLeft="4px solid"
              borderColor="green.400"
              pl={4}
            >
              {isEditMode ? "Editar Publicação" : "Nova Publicação no Blog"}
            </Heading>
            <Text color="gray.400" pl={4} mt={1} fontSize="0.9rem">
              {isEditMode ? "Atualize o conteúdo e salve" : "Escreva e publique seu texto"}
            </Text>
          </Box>
          <Button
            variant="outline"
            colorScheme="gray"
            size="sm"
            onClick={() => navigate("/allBlog")}
          >
            ← Voltar
          </Button>
        </Flex>

        <Box
          bg="white"
          borderRadius="1.25rem"
          boxShadow="0 2px 12px rgba(0,0,0,0.07)"
          border="1px solid"
          borderColor="gray.100"
          overflow="hidden"
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex flexDir="column" gap={0}>
              <Box px={6} py={5} borderBottom="1px solid" borderColor="gray.100">
                <FormLabel fontSize="0.82rem" fontWeight={700} color="gray.600" mb={2}>
                  TÍTULO DA PUBLICAÇÃO
                </FormLabel>
                <Input
                  {...register("title", { required: true })}
                  placeholder="Ex: Reflexões sobre o caminho maçônico"
                  bg="gray.50"
                  borderColor="gray.200"
                  fontSize="1.1rem"
                  fontWeight={600}
                  _focus={{ bg: "white", borderColor: "green.400" }}
                  size="lg"
                />
              </Box>

              <Box px={6} py={5} borderBottom="1px solid" borderColor="gray.100">
                <FormLabel fontSize="0.82rem" fontWeight={700} color="gray.600" mb={2}>
                  CONTEÚDO
                </FormLabel>
                <Box
                  borderRadius="0.5rem"
                  overflow="hidden"
                  border="1px solid"
                  borderColor="gray.200"
                  _focusWithin={{ borderColor: "green.400" }}
                >
                  <ReactQuill
                    value={text}
                    onChange={setText}
                    style={{ minHeight: "300px", fontFamily: "Afacad, sans-serif" }}
                  />
                </Box>
              </Box>

              <Box px={6} py={5}>
                <FormLabel fontSize="0.82rem" fontWeight={700} color="gray.600" mb={3}>
                  IMAGENS (opcional)
                </FormLabel>

                {existingImages.length > 0 && (
                  <>
                    <Text fontSize="0.78rem" color="gray.500" mb={2}>Imagens já salvas:</Text>
                    <Flex gap={3} wrap="wrap" mb={4}>
                      {existingImages.map((filename) => (
                        <Box key={filename} position="relative" borderRadius="0.75rem" overflow="hidden" border="2px solid" borderColor="green.200">
                          <Image
                            src={`${process.env.REACT_APP_S3_BASEURL}/news_images/${filename}`}
                            maxH="130px"
                            objectFit="cover"
                          />
                          <IconButton
                            colorScheme="red"
                            size="xs"
                            icon={<DeleteIcon />}
                            position="absolute"
                            top={1}
                            right={1}
                            borderRadius="50%"
                            onClick={() => setExistingImages((prev) => prev.filter((img) => img !== filename))}
                          />
                        </Box>
                      ))}
                    </Flex>
                    <Divider mb={4} />
                  </>
                )}

                <Box
                  {...getRootProps()}
                  border="2px dashed"
                  borderColor={isDragActive ? "green.400" : "gray.200"}
                  borderRadius="0.75rem"
                  p={6}
                  textAlign="center"
                  cursor="pointer"
                  bg={isDragActive ? "green.50" : "gray.50"}
                  _hover={{ borderColor: "green.300", bg: "green.50" }}
                  transition="all 0.2s"
                  mb={images.length > 0 ? 4 : 0}
                >
                  <input {...getInputProps()} />
                  <Box as={AttachmentIcon} boxSize={6} color="gray.400" mb={2} />
                  <Text color="gray.500" fontSize="0.9rem">
                    {isDragActive ? "Solte as imagens aqui..." : "Arraste imagens ou clique para selecionar"}
                  </Text>
                  <Text color="gray.400" fontSize="0.78rem" mt={1}>PNG, JPG, JPEG aceitos</Text>
                </Box>

                {images.length > 0 && (
                  <Flex gap={3} wrap="wrap">
                    {images.map((file) => (
                      <Box key={file.preview} position="relative" borderRadius="0.75rem" overflow="hidden" border="2px solid" borderColor="gray.200">
                        <Image src={file.preview} maxH="130px" objectFit="cover" />
                        <IconButton
                          colorScheme="red"
                          size="xs"
                          icon={<DeleteIcon />}
                          position="absolute"
                          top={1}
                          right={1}
                          borderRadius="50%"
                          onClick={() => setImages((prev) => prev.filter((img) => img.preview !== file.preview))}
                        />
                      </Box>
                    ))}
                  </Flex>
                )}
              </Box>

              <Flex px={6} py={4} borderTop="1px solid" borderColor="gray.100" justifyContent="flex-end" gap={3}>
                <Button variant="outline" colorScheme="gray" onClick={() => navigate("/allBlog")}>
                  Cancelar
                </Button>
                <Button isLoading={isLoading} type="submit" colorScheme="green" px={8}>
                  {isEditMode ? "Salvar Alterações" : "Publicar"}
                </Button>
              </Flex>
            </Flex>
          </form>
        </Box>
      </Box>
    </Layout>
  );
};

export default CreateBlog;
